import Icon from "@/components/Icon";
import { RadioOptionType, RadioTagGroup } from "@/components/Radio/RadioTag";
import {
  closeThirdPartyModelSelect,
  openThirdPartyModelSelect,
  setModelType,
  setSelectedThirdPartyModel,
  setThirdPartyModel,
  statusStore,
} from "@/stores/status";
import {
  Center,
  chakra,
  FlexProps,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Text,
  Portal,
  Box,
  Flex,
  useBoolean,
  MenuItem,
  Menu,
  MenuButton,
  MenuList,
  PopoverCloseButton,
  useBreakpoint,
  useMediaQuery,
} from "@chakra-ui/react";
import { langStore, translate } from "@noli/business";
import { observer } from "mobx-react-lite";
import React, { PropsWithChildren, useCallback, useEffect, useMemo, useState } from "react";
import { ReactComponent as HotSvg } from "@/assets/icons/hot.svg";
import { genBlock } from "@/utils/bem";
import {
  ModelTypes,
  ModelTypesWithThirdParty,
  MODEL_TYPES,
  MODEL_TYPES_INFO_WITH_THIRD_PARTY,
  MODEL_TYPES_WITH_THIRD_TYPES,
} from "@/modules/editor/constant";
import classNames from "classnames";
import { Editor_Words } from "@/utils/lang/editor";
import { isThirdParty, MultiModelInfo } from "@/thirdParty/constants";
import { ExpandableText } from "../../WordTable/ExpandableText";
import CardBoard, { CardBoardProps, ListData } from "../CardBoard";
import { ModelInfo, monkeyStore } from "@/thirdParty/monkey/store";
import { fetchModelList } from "@/thirdParty/monkey/api";
import { useTranslate } from "@/utils/lang";
import { genResponsivePx } from "@/stores/theme";
import { TriangleDownIcon } from "@chakra-ui/icons";
import { TooltipInfo } from "@/components/Icons";
import { LabelText } from "../../styleComponents/Texts";
import { User_Assets_Words } from "@/utils/lang/userAsset";
import { isAuthUser } from "@/utils/status";

// interface ExpandStatus {
//   monkey: boolean;
// }
// const defaultExpandStatus: ExpandStatus = {
//   monkey: false,
// };

type ModelSwitchProps = {
  direction?: FlexProps["flexDirection"];
  enableModelTypes?: ModelTypesWithThirdParty[];
  className?: string;
  value?: ModelTypesWithThirdParty;
  onChange?: (value: ModelTypesWithThirdParty) => void;
};

const block = genBlock("c-model-switch");
const IModelSwitch: React.FC<ModelSwitchProps> = ({
  direction = "row",
  enableModelTypes = MODEL_TYPES_WITH_THIRD_TYPES,
  className,
  value,
  onChange,
}) => {
  const lang = langStore.tgt;
  const [showSelector, showSelectorOp] = useBoolean(false);
  useEffect(() => {
    if (monkeyStore.modelList.length === 0) {
      fetchModelList().then((res: any) => {
        if (res.code === 0) {
          monkeyStore.setModelList(res.data.model_list);
        }
      });
    }
  }, [monkeyStore.modelList]);

  function getLabel(type: ModelTypesWithThirdParty): string {
    const modelTypeInfo = statusStore.thirdPartyModelInfo[type];
    const selected = statusStore.thirdPartyModelSelectInfo.model;
    if (modelTypeInfo.type === "multi") {
      if (selected && modelTypeInfo.value === "monkey") {
        return selected;
      } else if (modelTypeInfo.value !== "third-party") {
        const info = modelTypeInfo.infos.find((info) => info.value === modelTypeInfo.value)!;
        return info.label;
      } else {
        return modelTypeInfo.label;
      }
    }
    return statusStore.thirdPartyModelInfo[type]?.label;
  }
  const modelMapper = (it: ModelInfo) => {
    return {
      data: it,
      key: it.model_id,
      title: it.name,
      src: it.thumb_url,
      desc: it.desc,
    };
  };

  const modelList = useMemo<ListData<ModelInfo>[]>(() => {
    return monkeyStore.modelList.map(modelMapper);
  }, [monkeyStore.modelList]);
  const cardClick = useCallback(
    (modelInfo: ListData<any>) => {
      setThirdPartyModel("monkey");
      monkeyStore.setSelectedModel(modelInfo.data);
      setSelectedThirdPartyModel(modelInfo.title);
      showSelectorOp.off();
      closeThirdPartyModelSelect();
    },
    [statusStore.thirdPartyModelSelectInfo.model, showSelectorOp],
  );

  const options = useMemo(() => {
    return enableModelTypes.map((type) => {
      const option: RadioOptionType = {
        value: statusStore.thirdPartyModelInfo[type].value,
        label: (
          <>
            {isThirdParty(type) ? (
              <Popover
                isLazy
                placement="right"
                boundary={document.body}
                preventOverflow
                flip
                gutter={15}
                isOpen={statusStore.thirdPartyModelSelectInfo.open}
                onClose={() => {
                  showSelectorOp.off();
                  closeThirdPartyModelSelect();
                }}
                closeOnBlur={!showSelector}
                eventListeners={false}
                modifiers={[
                  {
                    name: "topLogger",
                    enabled: true,
                    phase: "main",
                    fn: ({ state }) => {
                      state.styles.popper.zIndex = "1001";
                      state.styles.popper.width = "max-content";
                      return state;
                    },
                  },
                ]}
              >
                <PopoverTrigger>
                  <Flex align="center" p="4px 8px" onClick={openThirdPartyModelSelect}>
                    <Text>{translate(getLabel(type), lang)}</Text>
                    <TriangleDownIcon boxSize="8px" transform="rotate(-90deg)" ml="2px" />
                  </Flex>
                </PopoverTrigger>
                <Portal>
                  <PopoverContent
                    maxW="210px"
                    p="10px"
                    fontSize="14px"
                    w="max-content"
                    border="none"
                    bg="bg.secondary"
                    shadow="0 0 16px 0 #00000044"
                    rounded="12px"
                    justifyContent="center"
                    zIndex={500}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <PopoverBody p="0px" w="100%" maxW="210px" h="100%">
                      {(statusStore.thirdPartyModelInfo["third-party"] as MultiModelInfo).infos.map(
                        (model) => {
                          return (
                            // eslint-disable-next-line react/jsx-key
                            <Text
                              display="flex"
                              alignItems="center"
                              px="8px"
                              py="6px"
                              key={model.value}
                              _hover={{
                                bg: "text.primary",
                              }}
                              _focus={{}}
                              rounded="4px"
                              overflow="hidden"
                              h="34px"
                              cursor="pointer"
                              userSelect="none"
                              color="text.secondary"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (model.value === "monkey") {
                                  showSelector ? showSelectorOp.off() : showSelectorOp.on();
                                } else {
                                  showSelectorOp.off();
                                  monkeyStore.setSelectedModel();
                                  setSelectedThirdPartyModel("");
                                  setThirdPartyModel(model.value);
                                  closeThirdPartyModelSelect();
                                }
                              }}
                            >
                              {model.value === "wenxin" || model.value === "draft" ? (
                                <Text>{translate(model.label, lang)}</Text>
                              ) : (
                                <CommunityModelSelector
                                  list={modelList}
                                  selected={
                                    monkeyStore.selectedModel &&
                                    modelMapper(monkeyStore.selectedModel)
                                  }
                                  isOpen={showSelector}
                                  cardBtnClick={cardClick}
                                  onClose={() => {
                                    showSelectorOp.off();
                                  }}
                                >
                                  <ExpandableText open={showSelector}>
                                    {translate(model.label, lang)}
                                  </ExpandableText>
                                </CommunityModelSelector>
                              )}
                            </Text>
                          );
                        },
                      )}
                    </PopoverBody>
                  </PopoverContent>
                </Portal>
              </Popover>
            ) : (
              <Text p="4px 8px">{translate(getLabel(type), lang)}</Text>
            )}
            {statusStore.thirdPartyModelInfo[type]?.isHot ? (
              <Center h="14px">
                <Icon
                  squared={false}
                  w="60px"
                  h="64px"
                  p="6px"
                  transition="all .3s cubic-bezier(.08,.52,.52,1)"
                  mt="2px"
                  ml="-2px"
                  mr="-4px"
                  svg={HotSvg}
                />
              </Center>
            ) : null}
          </>
        ),
        css: {
          p: "0px",
        },
      };
      return option;
    });
  }, [
    enableModelTypes,
    lang,
    statusStore.thirdPartyModelSelectInfo.open,
    statusStore.modelType,
    showSelector,
    showSelectorOp,
    modelList,
    monkeyStore.selectedModel,
    cardClick,
  ]);

  const defaultOnChange = (nextValue: ModelTypesWithThirdParty) => {
    setModelType(nextValue);
    monkeyStore.setSelectedModel();
  };

  return (
    <RadioTagGroup
      options={options}
      title={
        isAuthUser() ? (
          <TooltipInfo
            title={
              <LabelText color="white">{translate(Editor_Words["choose-a-model"], lang)}</LabelText>
            }
            info={
              <Flex
                flexDir="column"
                fontSize="14px"
                lineHeight="22px"
                color="white"
                whiteSpace="pre-line"
              >
                {translate(User_Assets_Words.coins_use_for_tip, lang)}
              </Flex>
            }
            tooltipProps={{
              arrowSize: 4,
              maxWidth: "402px",
            }}
          />
        ) : (
          <LabelText color="white">{translate(Editor_Words["choose-a-model"], lang)}：</LabelText>
        )
      }
      className={classNames(className, block())}
      flexDirection={direction}
      useRadioGroupProps={{
        value: value ?? statusStore.modelType,
        onChange: onChange ?? defaultOnChange,
      }}
    />
  );
};

interface CommunityModelSelectorModelProps extends CardBoardProps<any> {
  isOpen: boolean;
  onClose(): void;
}
function CommunityModelSelector(props: PropsWithChildren<CommunityModelSelectorModelProps>) {
  const {
    isOpen,
    onClose,
    children,
    list,
    cardBtnClick,
    searchFilter = (data, key) => {
      const titleMatch = data.title.includes(key);
      const descMatch = (data.desc ?? "").includes(key);
      return titleMatch || descMatch;
    },
    selected,
  } = props;
  const [isLarge] = useMediaQuery("(min-width: 1920px)");
  const { translate } = useTranslate();
  return (
    <Popover
      isLazy
      placement="right-start"
      boundary={document.body}
      preventOverflow
      flip
      gutter={25}
      isOpen={isOpen}
      onClose={onClose}
      closeOnBlur={true}
      eventListeners={false}
      modifiers={[
        {
          name: "topLogger",
          enabled: true,
          phase: "main",
          fn: ({ state }) => {
            state.styles.popper.zIndex = "1001";
            state.styles.popper.width = isLarge ? "1070px" : "802.5px";
            state.styles.popper.minWidth = isLarge ? "1070px" : "802.5px";
            return state;
          },
        },
      ]}
    >
      <PopoverTrigger>{children}</PopoverTrigger>
      <Portal>
        <PopoverContent
          border="none"
          bg="bg.secondary"
          shadow="0 0 16px 0 #00000044"
          w={genResponsivePx(1070)}
          h={genResponsivePx(533)}
          rounded="12px"
          justifyContent="center"
          zIndex={500}
          onClick={(e) => e.stopPropagation()}
        >
          <PopoverBody p="0px" w="100%" h="100%">
            <PopoverCloseButton color="white" />
            <CardBoard
              list={list}
              cardBtnClick={cardBtnClick}
              search
              selected={selected}
              searchFilter={searchFilter}
              rightHeader={
                <chakra.span fontWeight={600} fontSize="14px">
                  {translate(Editor_Words["community-model-provider"])}
                  <chakra.a
                    mx="0.5em"
                    href="https://frame.infmonkeys.com/"
                    target="_blank"
                    color="brand.primary"
                  >
                    {translate(Editor_Words["monkey-product-name"])}
                  </chakra.a>
                  {translate(Editor_Words["technique-support"])}
                </chakra.span>
              }
            />
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
}

export const ModelSwitch = chakra(observer(IModelSwitch));
